/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box, Flex, Text, Center, Input, Button, useToast, InputRightElement, InputGroup, FormLabel, Checkbox } from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import RadioFormField from '../../components/form/RadioFormField';
import TextFormField from '../../components/form/TextFormField';
import CheckListFormField from '../../components/form/CheckListFormFieldNested';
import SelectSearchFormField from '../../components/form/SelectSearchFormField';
import useStore from '../../store';
import c from '../../constant';
import { getPermissionValuesFromUser } from '../../utils/user-permission';
import * as Yup from 'yup';

const CONVERT_USER_TO_EMPLOYEE = gql`
	mutation convert($username: String!) {
		convertUserToEmployeeByUsername(username: $username)
	}
`;

const SIGNUP_MUTATION = gql`
	mutation signUp(
		$username: String!
		$password: String!
		$role: String
		$companyReportType: String
		$department: String
		$office: String
		$fullName: String
		$fullNameKhmer: String
		$canAssignUserToCompanyProposal: Boolean
		$canAssignUserToCompanyReport: Boolean
		$canUploadDoneFileToCompanyProposal: Boolean
		$canViewListReportToCompanyProposal: Boolean
		$canAutoreassignProposal: Boolean
		$financialReportTypes: String
		$canUpdateCompanyLicensing: Boolean
		$canUpdateCompanyGovernance: Boolean
		$canUpdateCompanyContact: Boolean
		$canApproveRejectLeaveRequest: Boolean
		$canViewLeaveRequestReport: Boolean
		$canNotApplyForLeave: Boolean
		$canReviewAgentRequest: Boolean
		$canApproveRejectDeactivateAgent: Boolean
		$canFinalApproveRejectAgent: Boolean
		$canUploadDoneFileToComplaint: Boolean
		$canAssignUserToComplaint: Boolean
		$canViewComplaintReport: Boolean
		$position: String
		$paymentAgentRegistration: Boolean
		$paymentPublicService: Boolean
		$paymentLicense: Boolean
		$canUpdateLicense: Boolean
		$paymentFine: Boolean
		$canUpdateFine: Boolean
		$canCreateIssuedLetter: Boolean
		$canUpdateIssuedLetter: Boolean
		$canDeleteIssuedLetter: Boolean
		$canSubmitTrainingCourse: Boolean
		$canExportTrainingCourse: Boolean
		$canApproveMissionRecord: Boolean
		$canExportMissionRecordData: Boolean
		$mdd: JSON
		$ews: JSON
		$nprp: JSON
	) {
		signUp(
			userInput: { username: $username, password: $password }
			role: $role
			companyReportType: $companyReportType
			department: $department
			office: $office
			fullName: $fullName
			fullNameKhmer: $fullNameKhmer
			canAssignUserToCompanyProposal: $canAssignUserToCompanyProposal
			canUploadDoneFileToCompanyProposal: $canUploadDoneFileToCompanyProposal
			canViewListReportToCompanyProposal: $canViewListReportToCompanyProposal
			canAutoreassignProposal: $canAutoreassignProposal
			financialReportTypes: $financialReportTypes
			canUpdateCompanyLicensing: $canUpdateCompanyLicensing
			canUpdateCompanyGovernance: $canUpdateCompanyGovernance
			canUpdateCompanyContact: $canUpdateCompanyContact
			canAssignUserToCompanyReport: $canAssignUserToCompanyReport
			canApproveRejectLeaveRequest: $canApproveRejectLeaveRequest
			canViewLeaveRequestReport: $canViewLeaveRequestReport
			canNotApplyForLeave: $canNotApplyForLeave
			canReviewAgentRequest: $canReviewAgentRequest
			canApproveRejectDeactivateAgent: $canApproveRejectDeactivateAgent
			canFinalApproveRejectAgent: $canFinalApproveRejectAgent
			canUploadDoneFileToComplaint: $canUploadDoneFileToComplaint
			canAssignUserToComplaint: $canAssignUserToComplaint
			canViewComplaintReport: $canViewComplaintReport
			position: $position
			paymentAgentRegistration: $paymentAgentRegistration
			paymentPublicService: $paymentPublicService
			paymentLicense: $paymentLicense
			canUpdateLicense: $canUpdateLicense
			paymentFine: $paymentFine
			canUpdateFine: $canUpdateFine
			canCreateIssuedLetter: $canCreateIssuedLetter
			canUpdateIssuedLetter: $canUpdateIssuedLetter
			canDeleteIssuedLetter: $canDeleteIssuedLetter
			canSubmitTrainingCourse: $canSubmitTrainingCourse
			canExportTrainingCourse: $canExportTrainingCourse
			canApproveMissionRecord: $canApproveMissionRecord
			canExportMissionRecordData: $canExportMissionRecordData
			mdd: $mdd
			ews: $ews
			nprp: $nprp
		)
	}
`;

// Source-user picker for "copy permissions from an existing user". Only the
// fields needed to identify someone in the dropdown — the full permission set
// is fetched separately, once a source is actually chosen.
const QUERY_USERS = gql`
	query getUsers($filterQuery: JSONObject, $offset: Float, $limit: Float, $name: String, $departments: String, $offices: String, $active: Boolean) {
		getUsers(
			filterQuery: $filterQuery
			pagerInput: { limit: $limit, offset: $offset }
			name: $name
			departments: $departments
			offices: $offices
			active: $active
		) {
			users {
				id
				username
				fullName
				fullNameKhmer
				role
				department
				office
				position
			}
			totalRows
		}
	}
`;

const QUERY_SOURCE_USER = gql`
	query user($id: String!) {
		getUserById(id: $id) {
			id
			username
			fullName
			role
			companyReportType
			department
			office
			position
			canAssignUserToCompanyProposal
			canAssignUserToCompanyReport
			canUploadDoneFileToCompanyProposal
			canViewListReportToCompanyProposal
			canAutoreassignProposal
			financialReportTypes
			canUpdateCompanyLicensing
			canUpdateCompanyGovernance
			canUpdateCompanyContact
			canApproveRejectLeaveRequest
			canViewLeaveRequestReport
			canNotApplyForLeave
			canReviewAgentRequest
			canApproveRejectDeactivateAgent
			canFinalApproveRejectAgent
			canUploadDoneFileToComplaint
			canAssignUserToComplaint
			canViewComplaintReport
			paymentAgentRegistration
			paymentPublicService
			paymentLicense
			canUpdateLicense
			paymentFine
			canUpdateFine
			canCreateIssuedLetter
			canUpdateIssuedLetter
			canDeleteIssuedLetter
			mdd
			ews
			nprp
		}
	}
`;

const pickerSelectStyles = {
	control: (provided) => ({ ...provided, borderRadius: 4, minHeight: 38, fontSize: 14 }),
	menu: (provided) => ({ ...provided, zIndex: 999, textAlign: 'left', fontSize: 14 }),
};

const signUpSchema = Yup.object().shape({
	fullName: Yup.string().required('Required'),
	fullNameKhmer: Yup.string().required('Required'),
	username: Yup.string()
		.required('Required')
		.matches(/^[a-z0-9._-]+$/, 'must be lowercase with no spaces and only ".", "-", "_" allowed'),
	password: Yup.string()
		.required('Required')
		.matches(
			/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/,
			'must be at least 8 characters long and include 1 uppercase, 1 lowercase, 1 number, and 1 symbol',
		),
	role: Yup.array().min(1, 'Required').required('Required'),
	companyReportType: Yup.array(),
	companyProposalPermission: Yup.array(),
	companyReportPermission: Yup.array(),
	complaintPermission: Yup.array(),
	department: Yup.array(),
	office: Yup.string(),
	financialReportTypes: Yup.array(),
	companyPermission: Yup.array(),
	requestLeavePermission: Yup.array(),
	agentPermission: Yup.array(),
	paymentPermission: Yup.array(),
	issuedLetter: Yup.array(),
	trainingCourse: Yup.array(),
	mdd: Yup.array(),
	ews: Yup.array(),
	nprp: Yup.array(),
	missionRecord: Yup.array(),
	position: Yup.object().nullable(),
});

function Create() {
	const history = useHistory();
	const toast = useToast();
	const { t } = useTranslation();
	const [tmpRole, setTmpRole] = useState([]);
	const [departmentCallback, setDepartmentCallback] = useState(null);
	const [tmpReportType, setTmpReportType] = useState([]);
	const [signUpUser, { loading, error, data }] = useMutation(SIGNUP_MUTATION);
	const [username, setUsername] = useState('');
	const [convertUserToEmployee] = useMutation(CONVERT_USER_TO_EMPLOYEE);
	const { currentLang } = useStore((state) => state.currentLang);

	// "Copy permissions from" state. `copiedValues` holds the permission arrays
	// pulled from the source user; `formKey` remounts the Formik tree whenever a
	// copy is applied, because CheckListFormField seeds its checkbox state from
	// `initValue` on mount only (it renders via CheckboxGroup's uncontrolled
	// `defaultValue`), so setFieldValue alone would update Formik's values
	// without ever ticking the visible boxes.
	const [sourceUserOption, setSourceUserOption] = useState(null);
	const [copiedValues, setCopiedValues] = useState(null);
	// Defaults to on: the usual reason to copy someone's permissions is that the
	// new user sits in the same department/office/position, and those three
	// fields also gate which nested permission checklists are visible.
	const [copyPlacement, setCopyPlacement] = useState(true);
	const [formKey, setFormKey] = useState(0);

	// Cascading source-user filters: Department -> Office -> Position -> Name.
	// Each narrows the staff query server-side (getUsers already supports
	// departments/offices/name directly, and position via filterQuery), so the
	// dropdown never has to hold the whole staff list client-side.
	const [filterDepartment, setFilterDepartment] = useState(null);
	const [filterOffice, setFilterOffice] = useState(null);
	const [filterPosition, setFilterPosition] = useState(null);
	const [filterName, setFilterName] = useState('');
	const [debouncedName, setDebouncedName] = useState('');
	const [loadUsers, { data: usersData, loading: loadingUsers }] = useLazyQuery(QUERY_USERS, { fetchPolicy: 'network-only' });
	const [loadSourceUser, { loading: loadingSourceUser }] = useLazyQuery(QUERY_SOURCE_USER, {
		fetchPolicy: 'network-only',
		onCompleted: (result) => {
			const sourceUser = result?.getUserById;
			if (!sourceUser) {
				toast({
					title: t('Could not load that user'),
					status: 'error',
					isClosable: true,
					position: 'top-right',
				});
				return;
			}
			const values = getPermissionValuesFromUser(sourceUser, {
				includePlacement: copyPlacement,
				positionOptions: getPositionOptions(c?.position),
			});
			setCopiedValues(values);
			setTmpRole(values.role || []);
			setTmpReportType(values.companyReportType || []);
			if (copyPlacement) {
				setDepartmentCallback(values.department || []);
			}
			setFormKey((key) => key + 1);
			toast({
				title: t('Permissions copied from') + ` ${sourceUser?.fullName || sourceUser?.username}`,
				description: t('Review the selections below before creating the user.'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
		},
		onError: () => {
			toast({
				title: t('Could not load that user'),
				status: 'error',
				isClosable: true,
				position: 'top-right',
			});
		},
	});

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedName(filterName.trim()), 400);
		return () => clearTimeout(timer);
	}, [filterName]);

	// Only query once a department is chosen — without it the result is just
	// "everyone", which is the unfiltered list this cascade exists to avoid.
	useEffect(() => {
		if (!filterDepartment?.value) return;
		const tokens = [];
		if (filterPosition?.value) {
			// `position` is a varchar column but c.position values are numbers —
			// stringify so the '=' comparison matches what signUp actually stored.
			tokens.push({ propertyKey: 'position', operator: '=', value: filterPosition.value.toString() });
		}
		loadUsers({
			variables: {
				departments: filterDepartment.value,
				offices: filterOffice?.value || null,
				name: debouncedName || null,
				active: true,
				filterQuery: tokens.length ? { tokens, operation: 'and' } : null,
				offset: 0,
				limit: 100,
			},
		});
	}, [filterDepartment, filterOffice, filterPosition, debouncedName]);

	function getSourceUserOptions() {
		const users = usersData?.getUsers?.users || [];
		return users.map((user) => {
			const placement = [getOfficeLabel(user?.department, user?.office), getPositionLabel(user?.position)].filter(Boolean).join(' / ');
			return {
				value: user?.id,
				label: `${user?.fullName || user?.username}${placement ? ` — ${placement}` : ''}`,
			};
		});
	}

	function getDepartmentFilterOptions() {
		return c.departments.map((d) => ({
			value: d.value,
			label: currentLang === 'en' ? d.label : d.labelKh || d.label,
		}));
	}

	function getOfficeFilterOptions() {
		const dept = c.departments.find((d) => d.value === filterDepartment?.value);
		return (dept?.offices || []).map((o) => ({
			value: o.value,
			label: currentLang === 'en' ? o.label : o.labelKh || o.label,
		}));
	}

	function getPositionLabel(position) {
		if (!position) return '';
		const match = c?.position?.find((p) => p?.value?.toString() === position?.toString());
		if (!match) return position;
		return currentLang === 'en' ? match.label : match.labelKh || match.label;
	}

	// Changing a filter invalidates anything downstream of it, including an
	// already-picked source user — otherwise the dropdown would keep showing a
	// selection that is no longer in the narrowed result set.
	function onDepartmentFilterChange(option) {
		setFilterDepartment(option);
		setFilterOffice(null);
		setFilterPosition(null);
		setFilterName('');
		setDebouncedName('');
		setSourceUserOption(null);
	}

	function onOfficeFilterChange(option) {
		setFilterOffice(option);
		setFilterPosition(null);
		setFilterName('');
		setDebouncedName('');
		setSourceUserOption(null);
	}

	function onPositionFilterChange(option) {
		setFilterPosition(option);
		setFilterName('');
		setDebouncedName('');
		setSourceUserOption(null);
	}

	function getOfficeLabel(department, office) {
		if (!office) return '';
		const first = department ? department.split(',')[0] : '';
		const dept = c.departments.find((d) => d.value === first);
		const match = dept?.offices?.find((o) => o.value === office);
		if (!match) return office;
		return currentLang === 'en' ? match.label : match.labelKh || match.label;
	}

	function clearCopiedPermissions() {
		setSourceUserOption(null);
		setCopiedValues(null);
		setTmpRole([]);
		setTmpReportType([]);
		setDepartmentCallback(null);
		setFormKey((key) => key + 1);
	}

	function resetSourceFilters() {
		setFilterDepartment(null);
		setFilterOffice(null);
		setFilterPosition(null);
		setFilterName('');
		setDebouncedName('');
		clearCopiedPermissions();
	}

	function signUp(value) {
		setUsername(value.username);
		const variables = {
			fullName: value.fullName,
			fullNameKhmer: value.fullNameKhmer,
			username: value.username,
			password: value.password,
			role: arrayRoleToString(value.role),
			companyReportType: arrayRoleToString(value.companyReportType),
			department: arrayRoleToString(value.department),
			office: value.office,
			canAssignUserToCompanyProposal: value.companyProposalPermission?.includes('canAssignUser') ? true : false,
			canUploadDoneFileToCompanyProposal: value.companyProposalPermission?.includes('canUploadDoneFile') ? true : false,
			canViewListReportToCompanyProposal: value.companyProposalPermission?.includes('canViewListReport') ? true : false,
			canAutoreassignProposal: value.companyProposalPermission?.includes('canAutoreassignProposal') ? true : false,
			canAssignUserToCompanyReport: value.companyReportPermission?.includes('canAssignUser') ? true : false,
			canUploadDoneFileToComplaint: value.complaintPermission?.includes('canUploadDoneFile'),
			canAssignUserToComplaint: value.complaintPermission?.includes('canAssignComplaint'),
			canViewComplaintReport: value.complaintPermission?.includes('canViewReportForComplaint') ? true : false,
			financialReportTypes: arrayRoleToString(value.financialReportTypes),

			canUpdateCompanyLicensing: arrayRoleToString(value.companyPermission).includes('canUpdateLicensing') ? true : false,
			canUpdateCompanyGovernance: arrayRoleToString(value.companyPermission).includes('canUpdateGovernance') ? true : false,
			canUpdateCompanyContact: arrayRoleToString(value.companyPermission).includes('canUpdateContact') ? true : false,

			canApproveRejectLeaveRequest: value.requestLeavePermission?.includes('canApproveRejectLeaveRequest') ? true : false,
			canViewLeaveRequestReport: value.requestLeavePermission?.includes('canViewLeaveRequestReport') ? true : false,
			canNotApplyForLeave: value.requestLeavePermission?.includes('canNotApplyForLeave') ? true : false,

			canReviewAgentRequest: value.agentPermission?.includes('canReviewAgentRequest') ? true : false,
			canApproveRejectDeactivateAgent: value.agentPermission?.includes('canApproveRejectDeactivateAgent') ? true : false,
			canFinalApproveRejectAgent: value.agentPermission?.includes('canFinalApproveRejectAgent') ? true : false,

			paymentAgentRegistration: value.paymentPermission?.includes('paymentAgentRegistration') ? true : false,
			paymentPublicService: value.paymentPermission?.includes('paymentPublicService') ? true : false,

			paymentLicense: value.paymentPermission?.includes('paymentLicense') ? true : false,
			canUpdateLicense: value.paymentPermission?.includes('canUpdateLicense') ? true : false,
			paymentFine: value.paymentPermission?.includes('paymentFine') ? true : false,
			canUpdateFine: value.paymentPermission?.includes('canUpdateFine') ? true : false,

			canCreateIssuedLetter: value?.issuedLetter?.includes('canCreateIssuedLetter'),
			canUpdateIssuedLetter: value?.issuedLetter?.includes('canUpdateIssuedLetter'),
			canDeleteIssuedLetter: value?.issuedLetter?.includes('canDeleteIssuedLetter'),

			canSubmitTrainingCourse: value?.trainingCourse?.includes('canSubmitTrainingCourse') ? true : false,
			canExportTrainingCourse: value?.trainingCourse?.includes('canExportTrainingCourse') ? true : false,

			canApproveMissionRecord: value?.role?.includes('missionRecord') && value?.missionRecord?.includes('canApproveMissionRecord') ? true : false,
			canExportMissionRecordData: value?.role?.includes('missionRecord') && value?.missionRecord?.includes('canExportMissionRecordData') ? true : false,

			mdd: value?.mdd || [],

			ews: value?.ewsPermission || [],

			nprp: value?.nprpPermission || [],

			position: value?.position?.value ? value?.position?.value?.toString() : '',
		};

		signUpUser({ variables });
	}

	function arrayRoleToString(role) {
		let result = '';
		if (role) {
			role?.forEach((item, index) => {
				if (index === 0) {
					result = item;
				} else {
					result += `,${item}`;
				}
			});
		}
		return result;
	}

	useEffect(() => {
		if (error) {
			error.graphQLErrors.map(({ message }, i) =>
				toast({
					title: t(message),
					status: 'error',
					isClosable: true,
					position: 'top-right',
				}),
			);
		}
	}, [error]);

	useEffect(() => {
		if (data) {
			convertUserToEmployee({
				variables: { username },
			});
			if (data?.signUp) {
				toast({
					title: t('User created successfully'),
					status: 'success',
					isClosable: true,
					position: 'top-right',
				});
				history.push('/users?page=1');
			}
		}
	}, [data]);

	function getRadioRoles() {
		const roles = [];
		c?.roles?.forEach((r) => {
			roles.push({ label: r.label, value: r.role });
		});
		return roles;
	}

	function checkForRole(role) {
		let res = false;
		tmpRole?.forEach((item) => {
			if (item === role) {
				res = true;
			}
		});

		return res;
	}

	function checkForReportType(type) {
		let res = false;
		tmpReportType.forEach((item) => {
			if (item === type) {
				res = true;
			}
		});
		return res;
	}

	function getOfficeDataFromDepartment(departmentData) {
		if (departmentData?.length === 1) {
			const dp = departmentData?.length > 0 ? departmentData[0] : null;
			let res = c.departments.filter((e) => e.value === dp);
			if (res?.length > 0) {
				return res[0]?.offices;
			} else {
				return [];
			}
		} else {
			return [];
		}
	}

	function getPositionOptions(data) {
		let res = [];
		data?.forEach((item) => {
			res.push({ value: item?.value, label: currentLang === 'en' ? item?.label : item?.labelKh });
		});
		return res;
	}

	return (
		<>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: t('Users'),
							path: '/users?page=1',
						},
						{
							name: t('Create New User'),
							path: '#',
						},
					]}
				/>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex bg="#FAFAFA" p="16px" pt="8px" pb="8px" mb="16px" justifyContent="space-between" borderBottom="1px solid #dbdbdb">
							<Text fontSize="x-large" fontWeight="bold">
								{t('Create New User')}
							</Text>
						</Flex>

						<Flex
							p="16px"
							gap="24px"
							align="flex-start"
							direction={{ base: 'column', lg: 'row' }}
							justifyContent={{ base: 'flex-start', lg: 'space-between' }}
						>
							<Box flex="0 0 auto" maxW="480px" w="100%" order={{ base: 2, lg: 1 }}>
								<Formik
									key={formKey}
									initialValues={{
										username: '',
										password: '',
										role: '',
										companyReportType: '',
										department: '',
										office: '',
										position: '',
										...(copiedValues || {}),
									}}
									validationSchema={signUpSchema}
									onSubmit={(values) => {
										signUp(values);
									}}
								>
									{(props) => (
										<Form>
											<Box mt="3">
												<TextFormField {...props} label="Full Name In Latin" name="fullName" required />

												<TextFormField {...props} label="Full Name In Khmer" name="fullNameKhmer" required />

												<Field name="username">
													{({ field, meta }) => (
														<>
															<FormLabel>
																{t('Username')}
																<span style={{ color: 'red' }}>*</span>
															</FormLabel>
															<Input {...field} id="username" size="lg" fontWeight="500" autoComplete="off" />
															{meta.touched && meta.error && (
																<Text id="error-message-username" color="red.400" fontSize="12px">
																	{t('Username')} {t('is')} {t(meta.error)}
																</Text>
															)}
														</>
													)}
												</Field>

												<Field name="password">
													{({ field, meta, form }) => (
														<>
															<FormLabel mt="4">
																{t('Password')}
																<span style={{ color: 'red' }}>*</span>
															</FormLabel>
															<InputGroup size="md">
																<Input {...field} id="password" size="lg" fontWeight="500" autoComplete="off" />
																<InputRightElement width="4.5rem">
																	<Button
																		mt="8px"
																		mr="6px"
																		h="100%"
																		size="sm"
																		onClick={() => {
																			form.setFieldValue('password', generatePassword(), true);
																		}}
																	>
																		{t('Generate')}
																	</Button>
																</InputRightElement>
															</InputGroup>

															{meta.touched && meta.error && (
																<Text id="error-message-password" color="red.400" fontSize="12px">
																	{t('Password')} {t('is')} {t(meta.error)}
																</Text>
															)}
														</>
													)}
												</Field>

												<CheckListFormField
													label="Department"
													name="department"
													initValue={copiedValues?.department || []}
													list={c.departments}
													callBack={(e) => {
														setDepartmentCallback(e);
													}}
												/>

												{departmentCallback?.length === 1 && (
													<RadioFormField
														label="Office"
														name="office"
														initValue={copiedValues?.office || ''}
														radios={getOfficeDataFromDepartment(departmentCallback)}
													/>
												)}

												<SelectSearchFormField
													{...props}
													name="position"
													label="Position"
													placeholder="Position"
													options={getPositionOptions(c?.position)}
													t={t}
												/>

												<CheckListFormField
													label="Role"
													name="role"
													initValue={copiedValues?.role || []}
													list={getRadioRoles()}
													required
													callBack={(e) => setTmpRole(e)}
													nested={[
														{
															index: 3,
															child: (
																<>
																	{checkForRole('complaint') && (
																		<CheckListFormField
																			label="Complaint Permission"
																			name="complaintPermission"
																			initValue={copiedValues?.complaintPermission || []}
																			list={[
																				{
																					label: 'Can Assign',
																					value: 'canAssignComplaint',
																				},
																				{
																					label: 'Can upload done file',
																					value: 'canUploadDoneFile',
																				},
																				{
																					label: 'Can View Report',
																					value: 'canViewReportForComplaint',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 4,
															child: (
																<>
																	{checkForRole('companySubmissionReport') && (
																		<CheckListFormField
																			label="Company Report Types"
																			name="companyReportType"
																			initValue={copiedValues?.companyReportType || []}
																			list={c.companyReportTypes}
																			callBack={(e) => setTmpReportType(e)}
																			nested={[
																				{
																					index: 0,
																					child: (
																						<>
																							{checkForReportType('FINANCIAL_REPORT') && (
																								<CheckListFormField
																									label="Financial Report Types"
																									name="financialReportTypes"
																									initValue={copiedValues?.financialReportTypes || []}
																									list={c.financialReportTypes}
																								/>
																							)}
																						</>
																					),
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 4,
															child: (
																<>
																	{checkForRole('companySubmissionReport') && (
																		<CheckListFormField
																			label="Company Report Permission"
																			name="companyReportPermission"
																			initValue={copiedValues?.companyReportPermission || []}
																			list={[
																				{
																					label: 'Can Assign User',
																					value: 'canAssignUser',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 5,
															child: (
																<>
																	{checkForRole('companySubmissionProposal') && (
																		<CheckListFormField
																			label="Proposal Permission"
																			name="companyProposalPermission"
																			initValue={copiedValues?.companyProposalPermission || []}
																			list={[
																				{
																					label: 'Can Assign User',
																					value: 'canAssignUser',
																				},
																				{
																					label: 'Can upload done file',
																					value: 'canUploadDoneFile',
																				},
																				{
																					label: 'Can View List Report',
																					value: 'canViewListReport',
																				},
																				{
																					label: 'Can Auto-reassign Proposals',
																					value: 'canAutoreassignProposal',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 6,
															child: (
																<>
																	{checkForRole('companyList') && (
																		<CheckListFormField
																			label="Company Permission"
																			name="companyPermission"
																			initValue={copiedValues?.companyPermission || []}
																			list={[
																				{
																					label: 'Can Update Company Licensing',
																					value: 'canUpdateLicensing',
																				},
																				{
																					label: 'Can Update Company Governance',
																					value: 'canUpdateGovernance',
																				},
																				{
																					label: 'Can Update Company Contact',
																					value: 'canUpdateContact',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 7,
															child: (
																<>
																	{checkForRole('requestLeave') && (
																		<CheckListFormField
																			label="Request Leave Permission"
																			name="requestLeavePermission"
																			initValue={copiedValues?.requestLeavePermission || []}
																			list={[
																				{
																					label: 'Can Approve/Reject',
																					value: 'canApproveRejectLeaveRequest',
																				},
																				{
																					label: 'Can View Report',
																					value: 'canViewLeaveRequestReport',
																				},
																				{
																					label: 'Can not apply for leave',
																					value: 'canNotApplyForLeave',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 8,
															child: (
																<>
																	{checkForRole('agent') && (
																		<CheckListFormField
																			label="Agent Permission"
																			name="agentPermission"
																			initValue={copiedValues?.agentPermission || []}
																			list={[
																				{
																					label: 'Can Review Request',
																					value: 'canReviewAgentRequest',
																				},
																				{
																					label: 'Can Approve/Reject/Deactivate',
																					value: 'canApproveRejectDeactivateAgent',
																				},
																				{
																					label: 'Can Final Approve/Reject/Deactivate',
																					value: 'canFinalApproveRejectAgent',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 10,
															child: (
																<>
																	{checkForRole('payment') && (
																		<CheckListFormField
																			label="Payment Permission"
																			name="paymentPermission"
																			initValue={copiedValues?.paymentPermission || []}
																			list={[
																				{
																					label: 'Agent Registration',
																					value: 'paymentAgentRegistration',
																				},
																				{
																					label: 'Public Service',
																					value: 'paymentPublicService',
																				},
																				{
																					label: 'View License',
																					value: 'paymentLicense',
																				},
																				{
																					label: 'Can Update License',
																					value: 'canUpdateLicense',
																				},
																				{
																					label: 'View Fines',
																					value: 'paymentFine',
																				},
																				{
																					label: 'Can Update Fines',
																					value: 'canUpdateFine',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 11,
															child: (
																<>
																	{checkForRole('issuedLetter') && (
																		<CheckListFormField
																			label="Permission"
																			name="issuedLetter"
																			initValue={copiedValues?.issuedLetter || []}
																			list={[
																				{
																					label: 'Create',
																					value: 'canCreateIssuedLetter',
																				},
																				{
																					label: 'Edit',
																					value: 'canUpdateIssuedLetter',
																				},
																				{
																					label: 'Delete',
																					value: 'canDeleteIssuedLetter',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 14,
															child: (
																<>
																	{checkForRole('mdd') && (
																		<CheckListFormField
																			label="Permission"
																			name="mdd"
																			initValue={copiedValues?.mdd || []}
																			list={[
																				{
																					label: 'General Monthly Report',
																					value: 'mddgmr',
																				},
																				{
																					label: 'Life Monthly Report',
																					value: 'mddlmr',
																				},
																				{
																					label: 'Micro Monthly Report',
																					value: 'mddmmr',
																				},
																				{
																					label: 'Can View Report Data',
																					value: 'mmdrd',
																				},
																				{
																					label: 'Can Allow For Editing',
																					value: 'mddcae',
																				},
																				{
																					label: 'Can View Contribution Data 0.5%',
																					value: 'mmdcd',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 16,
															child: (
																<>
																	{checkForRole('ews') && (
																		<CheckListFormField
																			label="Permission"
																			name="ewsPermission"
																			initValue={copiedValues?.ewsPermission || []}
																			list={[
																				{
																					label: 'Can view statement of general insurance',
																					value: 'ewsCanViewStatementGeneralInsurance',
																				},
																				{
																					label: 'Can view statement of life insurance',
																					value: 'ewsCanViewStatementLifeInsurance',
																				},
																				{
																					label: 'Can view statement of micro insurance',
																					value: 'ewsCanViewStatementMicroInsurance',
																				},
																				{
																					label: 'Can view statement of reinsurance',
																					value: 'ewsCanViewStatementReinsurance',
																				},
																				{
																					label: 'Can view summarized report',
																					value: 'ewsCanViewSummarizedReport',
																				},
																				{
																					label: 'Can Assign User',
																					value: 'ewsCanAssignUser',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 20,
															child: (
																<>
																	{checkForRole('missionRecord') && (
																		<CheckListFormField
																			label="Permission"
																			name="missionRecord"
																			list={[
																				{
																					label: 'Can Approve/Acknowledge/Reject Mission Record',
																					value: 'canApproveMissionRecord',
																				},
																				{
																					label: 'Can Export Mission Record Data',
																					value: 'canExportMissionRecordData',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 21,
															child: (
																<>
																	{checkForRole('nprp') && (
																		<CheckListFormField
																			label="Permission"
																			name="nprpPermission"
																			initValue={copiedValues?.nprpPermission || []}
																			list={[
																				{
																					label: 'Can set color',
																					value: 'nprpSetColor',
																				},
																				{
																					label: 'Can change status to printed hard copy',
																					value: 'nprpPrintStatus',
																				},
																				{
																					label: 'AI Spell Checker',
																					value: 'nprpAiSpellChecker',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
														{
															index: 22,
															child: (
																<>
																	{checkForRole('trainingCourse') && (
																		<CheckListFormField
																			label="Permission"
																			name="trainingCourse"
																			list={[
																				{
																					label: 'Can Submit Training Course',
																					value: 'canSubmitTrainingCourse',
																				},
																				{
																					label: 'Can Export Training Course',
																					value: 'canExportTrainingCourse',
																				},
																			]}
																		/>
																	)}
																</>
															),
														},
													]}
												/>
												<Flex textAlign="right" mt="6" justifyContent="space-between" alignItems="center">
													<Button
														_hover={{ bg: 'primary.dark' }}
														id="login-btn"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														bg="primary.dark"
														color="white"
														type="submit"
														isLoading={loading}
														loadingText={t('CREATE')}
														spinnerPlacement="start"
													>
														{t(`CREATE`)}
													</Button>
												</Flex>
											</Box>
										</Form>
									)}
								</Formik>
							</Box>

							<Box
								flex="0 0 auto"
								w={{ base: '100%', lg: '420px' }}
								maxW="100%"
								order={{ base: 1, lg: 2 }}
								p="12px"
								bg="#FAFAFA"
								border="1px solid #dbdbdb"
								borderRadius={c.borderRadius}
								position={{ base: 'static', lg: 'sticky' }}
								top="16px"
							>
								<FormLabel mb="4px">{t('Copy permissions from an existing user')}</FormLabel>
								<Text fontSize="12px" color="gray.600" mb="10px">
									{t(
										'Optional. Narrow down to a similar staff member, then copy their permissions — name, username and password are never copied.',
									)}
								</Text>

								<Text fontSize="12px" fontWeight="600" mb="2px">
									{t('1. Department')}
								</Text>
								<Select
									isClearable
									isSearchable
									placeholder={t('Select department')}
									options={getDepartmentFilterOptions()}
									value={filterDepartment}
									onChange={onDepartmentFilterChange}
									styles={pickerSelectStyles}
								/>

								<Text fontSize="12px" fontWeight="600" mt="8px" mb="2px" color={filterDepartment ? 'inherit' : 'gray.400'}>
									{t('2. Office')}
								</Text>
								<Select
									isClearable
									isSearchable
									isDisabled={!filterDepartment}
									placeholder={filterDepartment ? t('Any office') : t('Select a department first')}
									options={getOfficeFilterOptions()}
									value={filterOffice}
									onChange={onOfficeFilterChange}
									styles={pickerSelectStyles}
								/>

								<Text fontSize="12px" fontWeight="600" mt="8px" mb="2px" color={filterDepartment ? 'inherit' : 'gray.400'}>
									{t('3. Position')}
								</Text>
								<Select
									isClearable
									isSearchable
									isDisabled={!filterDepartment}
									placeholder={filterDepartment ? t('Any position') : t('Select a department first')}
									options={getPositionOptions(c?.position)}
									value={filterPosition}
									onChange={onPositionFilterChange}
									styles={pickerSelectStyles}
								/>

								<Text fontSize="12px" fontWeight="600" mt="8px" mb="2px" color={filterDepartment ? 'inherit' : 'gray.400'}>
									{t('4. Staff member')}
								</Text>
								<Input
									size="sm"
									mb="6px"
									borderRadius={c.borderRadius}
									placeholder={filterDepartment ? t('Filter by name (optional)') : t('Select a department first')}
									isDisabled={!filterDepartment}
									value={filterName}
									onChange={(e) => setFilterName(e.target.value)}
								/>
								<Select
									isClearable
									isSearchable
									isDisabled={!filterDepartment}
									isLoading={loadingUsers || loadingSourceUser}
									placeholder={
										!filterDepartment
											? t('Select a department first')
											: loadingUsers
												? t('Loading staff')
												: getSourceUserOptions().length === 0
													? t('No staff match these filters')
													: t('Select staff to copy from')
									}
									noOptionsMessage={() => t('No staff match these filters')}
									options={getSourceUserOptions()}
									value={sourceUserOption}
									onChange={(option) => {
										setSourceUserOption(option);
										if (option?.value) {
											loadSourceUser({ variables: { id: option.value } });
										} else {
											clearCopiedPermissions();
										}
									}}
									styles={pickerSelectStyles}
								/>
								{filterDepartment && !loadingUsers && (
									<Text fontSize="11px" color="gray.500" mt="4px">
										{getSourceUserOptions().length} {t('staff match')}
										{usersData?.getUsers?.totalRows > getSourceUserOptions().length
											? ` ${t('(of')} ${usersData?.getUsers?.totalRows} — ${t('narrow further to see the rest)')}`
											: ''}
									</Text>
								)}

								<Checkbox mt="10px" colorScheme="green" isChecked={copyPlacement} onChange={(e) => setCopyPlacement(e.target.checked)}>
									<Text fontSize="13px">{t('Also copy department, office and position')}</Text>
								</Checkbox>
								{copiedValues && (
									<Flex mt="10px" alignItems="center" justifyContent="space-between">
										<Text fontSize="12px" color="green.700">
											{t('Permissions applied — review before creating.')}
										</Text>
										<Button size="xs" variant="outline" onClick={resetSourceFilters}>
											{t('Reset')}
										</Button>
									</Flex>
								)}
							</Box>
						</Flex>
					</Box>
				</Center>
			</Container>
		</>
	);
}

export default Create;

const generatePassword = (length = 8) => {
	const lowercase = 'abcdefghijklmnopqrstuvwxyz';
	const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
	const digits = '0123456789';
	const symbols = '~!@-#$';
	const all = lowercase + uppercase + digits + symbols;

	if (length < 8) {
		throw new Error('Password must be at least 8 characters long');
	}

	// Ensure at least one of each required character
	const required = [
		lowercase[Math.floor(Math.random() * lowercase.length)],
		uppercase[Math.floor(Math.random() * uppercase.length)],
		digits[Math.floor(Math.random() * digits.length)],
		symbols[Math.floor(Math.random() * symbols.length)],
	];

	// Fill the rest with random characters from the full set
	const remainingLength = length - required.length;
	const randomChars = Array.from(crypto.getRandomValues(new Uint32Array(remainingLength))).map((x) => all[x % all.length]);

	// Combine and shuffle
	const fullPassword = [...required, ...randomChars];
	for (let i = fullPassword.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[fullPassword[i], fullPassword[j]] = [fullPassword[j], fullPassword[i]];
	}

	return fullPassword.join('');
};
